/**
 * Auditor Settings Page
 *
 * Allows auditor users to manage their account settings, preferences,
 * professional credentials, and audit scheduling preferences.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, User, Bell, Lock, Award, Globe, Calendar } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';

export const AuditorSettingsPage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor']);
  const [activeTab, setActiveTab] = useState<'profile' | 'credentials' | 'scheduling' | 'notifications' | 'security' | 'preferences'>('profile');

  // Mock data - would come from API
  const [formData, setFormData] = useState({
    // Profile
    name: 'Sarah Ahmad',
    email: 'sarah.ahmad@jakim.gov.my',
    phone: '+60123456789',
    position: 'Senior Auditor',

    // Credentials
    auditorId: 'AUD-2020-001',
    certificationDate: '2020-01-15',
    expiryDate: '2026-01-15',
    specializations: 'Food Manufacturing, Restaurant Services',
    auditTypes: ['Initial Audit', 'Surveillance', 'Renewal'],

    // Scheduling
    maxAuditsPerMonth: '8',
    preferredDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
    workingHoursStart: '09:00',
    workingHoursEnd: '17:00',
    serviceAreas: 'Selangor, Kuala Lumpur, Putrajaya',

    // Notifications
    emailNotifications: true,
    auditAssignments: true,
    applicationUpdates: true,
    deadlineReminders: true,

    // Preferences
    language: 'en-US',
    timezone: 'Asia/Kuala_Lumpur',
    dateFormat: 'DD/MM/YYYY',
  });

  const handleSave = () => {
    // API call to save settings
    alert(t('common:messages.saveSuccess'));
  };

  const tabs = [
    { id: 'profile', label: 'User Profile', icon: <User className="w-4 h-4" /> },
    { id: 'credentials', label: 'Credentials', icon: <Award className="w-4 h-4" /> },
    { id: 'scheduling', label: 'Scheduling', icon: <Calendar className="w-4 h-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Lock className="w-4 h-4" /> },
    { id: 'preferences', label: 'Preferences', icon: <Globe className="w-4 h-4" /> },
  ];

  return (
    <PageLayout
      pageTitle={t('common:navigation.settings')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('common:navigation.settings') },
      ]}
    >
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:w-64 shrink-0">
          <Card>
            <CardBody className="p-2">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </CardBody>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">User Profile</h3>
                <p className="text-sm text-gray-600">Manage your personal information</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Position/Title
                      </label>
                      <input
                        type="text"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Credentials Tab */}
          {activeTab === 'credentials' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Auditor Credentials</h3>
                <p className="text-sm text-gray-600">Manage your certification and credentials</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Auditor ID
                      </label>
                      <input
                        type="text"
                        value={formData.auditorId}
                        onChange={(e) => setFormData({ ...formData, auditorId: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Certification Date
                      </label>
                      <input
                        type="date"
                        value={formData.certificationDate}
                        onChange={(e) => setFormData({ ...formData, certificationDate: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Certification Expiry Date
                      </label>
                      <input
                        type="date"
                        value={formData.expiryDate}
                        onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specializations
                      </label>
                      <input
                        type="text"
                        value={formData.specializations}
                        onChange={(e) => setFormData({ ...formData, specializations: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="e.g., Food Manufacturing, Restaurant Services"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Audit Types Qualified For
                      </label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {['Initial Audit', 'Surveillance', 'Renewal', 'Special Audit'].map((type) => (
                          <label key={type} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.auditTypes.includes(type)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, auditTypes: [...formData.auditTypes, type] });
                                } else {
                                  setFormData({
                                    ...formData,
                                    auditTypes: formData.auditTypes.filter((t) => t !== type),
                                  });
                                }
                              }}
                              className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Scheduling Tab */}
          {activeTab === 'scheduling' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Scheduling Preferences</h3>
                <p className="text-sm text-gray-600">Manage your availability and audit capacity</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum Audits per Month
                      </label>
                      <input
                        type="number"
                        value={formData.maxAuditsPerMonth}
                        onChange={(e) => setFormData({ ...formData, maxAuditsPerMonth: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Areas
                      </label>
                      <input
                        type="text"
                        value={formData.serviceAreas}
                        onChange={(e) => setFormData({ ...formData, serviceAreas: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="e.g., Selangor, Kuala Lumpur"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Working Hours Start
                      </label>
                      <input
                        type="time"
                        value={formData.workingHoursStart}
                        onChange={(e) => setFormData({ ...formData, workingHoursStart: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Working Hours End
                      </label>
                      <input
                        type="time"
                        value={formData.workingHoursEnd}
                        onChange={(e) => setFormData({ ...formData, workingHoursEnd: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Working Days
                      </label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                          <label key={day} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.preferredDays.includes(day)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, preferredDays: [...formData.preferredDays, day] });
                                } else {
                                  setFormData({
                                    ...formData,
                                    preferredDays: formData.preferredDays.filter((d) => d !== day),
                                  });
                                }
                              }}
                              className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Notification Preferences</h3>
                <p className="text-sm text-gray-600">Manage how you receive notifications</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">Email Notifications</p>
                      <p className="text-sm text-gray-600">Receive notifications via email</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.emailNotifications}
                      onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">Audit Assignments</p>
                      <p className="text-sm text-gray-600">Get notified when assigned to new audits</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.auditAssignments}
                      onChange={(e) => setFormData({ ...formData, auditAssignments: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">Application Updates</p>
                      <p className="text-sm text-gray-600">Receive updates about application status changes</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.applicationUpdates}
                      onChange={(e) => setFormData({ ...formData, applicationUpdates: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium text-gray-900">Deadline Reminders</p>
                      <p className="text-sm text-gray-600">Get reminders about report submission deadlines</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.deadlineReminders}
                      onChange={(e) => setFormData({ ...formData, deadlineReminders: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Security Settings</h3>
                <p className="text-sm text-gray-600">Manage your password and security options</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      Update Password
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">User Preferences</h3>
                <p className="text-sm text-gray-600">Customize your experience</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language
                    </label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="en-US">English</option>
                      <option value="ms-MY">Bahasa Melayu</option>
                      <option value="zh-CN">简体中文</option>
                      <option value="ar-SA">العربية</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone
                    </label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="Asia/Kuala_Lumpur">Kuala Lumpur (GMT+8)</option>
                      <option value="Asia/Singapore">Singapore (GMT+8)</option>
                      <option value="Asia/Jakarta">Jakarta (GMT+7)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date Format
                    </label>
                    <select
                      value={formData.dateFormat}
                      onChange={(e) => setFormData({ ...formData, dateFormat: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
